import { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, Trash2 } from 'lucide-react';

interface RemoteShellViewerProps {
  tunnelUrl: string;
  sessionToken: string;
  onDisconnect: () => void;
  onError: (error: string) => void;
}

type ConnectionState = 'connecting' | 'connected' | 'disconnected';

interface OutputLine {
  type: 'prompt' | 'output' | 'error' | 'system';
  text: string;
}

export default function RemoteShellViewer({ tunnelUrl, sessionToken, onDisconnect, onError }: RemoteShellViewerProps) {
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sinceRef = useRef(0);
  const failCountRef = useRef(0);
  const stoppedRef = useRef(false);

  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [lines, setLines] = useState<OutputLine[]>([]);
  const [cwd, setCwd] = useState('C:\\');
  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [, setHistoryIndex] = useState(-1);
  const [running, setRunning] = useState(false);

  // Stable refs
  const onErrorRef = useRef(onError);
  const onDisconnectRef = useRef(onDisconnect);
  onErrorRef.current = onError;
  onDisconnectRef.current = onDisconnect;
  const pollUrlRef = useRef(`${tunnelUrl}/shell-poll?token=${sessionToken}`);
  const inputUrlRef = useRef(`${tunnelUrl}/shell-input?token=${sessionToken}`);

  // Auto-scroll to bottom
  useEffect(() => {
    const el = outputRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  // Focus input on click anywhere in the terminal
  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  // ── Polling loop ───────────────────────────────────────────────────────

  useEffect(() => {
    stoppedRef.current = false;
    sinceRef.current = 0;
    failCountRef.current = 0;

    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const resp = await fetch(`${pollUrlRef.current}&since=${sinceRef.current}`);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        failCountRef.current = 0;

        // Update connection state on first successful poll
        if (data.ready) {
          setConnectionState('connected');
        }

        // Append new lines
        if (data.lines && data.lines.length > 0) {
          setLines(prev => [...prev, ...data.lines]);
          sinceRef.current = data.total;
        }

        // Update cwd and running state
        setCwd(data.cwd || 'C:\\');
        setRunning(data.running || false);

      } catch {
        failCountRef.current++;
        // Allow ~30s of failures (60 attempts * 500ms) for cold start
        if (failCountRef.current > 60) {
          setConnectionState('disconnected');
          onErrorRef.current('Connection lost — station may have restarted');
          onDisconnectRef.current();
          return;
        }
      }

      if (!stoppedRef.current) {
        pollRef.current = setTimeout(poll, 500);
      }
    };

    poll();

    return () => {
      stoppedRef.current = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
   
  }, [tunnelUrl, sessionToken]);

  // ── Send command ────────────────────────────────────────────────────────

  const sendCommand = useCallback((command: string) => {
    if (!command.trim()) return;
    fetch(inputUrlRef.current, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command }),
    }).catch(() => {
      setLines(prev => [...prev, { type: 'error', text: 'Failed to send command' }]);
      setRunning(false);
    });
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const cmd = inputValue.trim();
    if (!cmd || running) return;
    sendCommand(cmd);
    setHistory(prev => [cmd, ...prev.filter(h => h !== cmd)].slice(0, 100));
    setHistoryIndex(-1);
    setInputValue('');
  }, [inputValue, running, sendCommand]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHistoryIndex(prev => {
        const next = Math.min(prev + 1, history.length - 1);
        if (next >= 0 && history[next]) setInputValue(history[next]);
        return next;
      });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHistoryIndex(prev => {
        const next = prev - 1;
        if (next < 0) { setInputValue(''); return -1; }
        if (history[next]) setInputValue(history[next]);
        return next;
      });
    }
  }, [history]);

  const handleDisconnect = useCallback(() => {
    stoppedRef.current = true;
    if (pollRef.current) clearTimeout(pollRef.current);
    onDisconnectRef.current();
  }, []);

  const clearOutput = useCallback(() => {
    setLines([]);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col bg-gray-950 rounded-lg overflow-hidden border border-gray-800">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 text-white text-xs border-b border-gray-800">
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${
            connectionState === 'connected' ? 'bg-green-500' :
            connectionState === 'connecting' ? 'bg-blue-500 animate-pulse' :
            'bg-red-500'
          }`} />
          <span className="text-gray-400 font-mono">
            {connectionState === 'connected' ? 'Shell' :
             connectionState === 'connecting' ? 'Connecting...' : 'Disconnected'}
          </span>
          {connectionState === 'connected' && (
            <span className="text-gray-600 font-mono ml-1">| {cwd}</span>
          )}
        </div>

        <div className="flex-1" />

        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={clearOutput} title="Clear output">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>

        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-gray-800"
          onClick={handleDisconnect} title="Disconnect">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Terminal output */}
      <div
        ref={outputRef}
        className="flex-1 overflow-y-auto p-3 font-mono text-[13px] leading-5 min-h-[300px] max-h-[500px] cursor-text select-text"
        onClick={focusInput}
      >
        {connectionState === 'connecting' ? (
          <div className="text-gray-500 flex items-center gap-2">
            <span className="h-3 w-3 rounded-full border-2 border-gray-500 border-t-transparent animate-spin" />
            Starting shell (may take 10-15s on cold start)...
          </div>
        ) : (
          lines.map((line, i) => (
            <div key={i} className="whitespace-pre-wrap break-all">
              {line.type === 'prompt' && (
                <span className="text-blue-400">PS {cwd}&gt; <span className="text-gray-200">{line.text}</span></span>
              )}
              {line.type === 'output' && (
                <span className="text-gray-300">{line.text}</span>
              )}
              {line.type === 'error' && (
                <span className="text-red-400">{line.text}</span>
              )}
              {line.type === 'system' && (
                <span className="text-gray-600 italic">{line.text}</span>
              )}
            </div>
          ))
        )}
        {running && (
          <div className="text-gray-500 flex items-center gap-1.5 mt-1">
            <span className="h-2 w-2 rounded-full border border-gray-500 border-t-transparent animate-spin" />
            Running...
          </div>
        )}
      </div>

      {/* Input */}
      {connectionState === 'connected' && (
        <form onSubmit={handleSubmit} className="flex items-center border-t border-gray-800 bg-gray-900/50">
          <span className="pl-3 text-blue-400 font-mono text-[13px] whitespace-nowrap select-none">
            PS {cwd}&gt;
          </span>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={running}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            className="flex-1 bg-transparent text-gray-200 font-mono text-[13px] px-2 py-2 outline-none placeholder:text-gray-700 disabled:opacity-50"
            placeholder={running ? 'Waiting for command to finish...' : 'Type a PowerShell command...'}
          />
        </form>
      )}
    </div>
  );
}
